import { formatMinor } from '@/kova/format'
import type { ApiClient } from '@/lib/api'
import { ApiError, isAuthzOutcome } from '@/lib/api/errors'
import { exactMinor, isCurrencyCode, type MinorWire } from '@/lib/money/money'

import { readAtStamp } from './provenance'

/**
 * The decision row behind a refusal, read back from
 * `GET /v1/admin/decisions/{ref}` (go-wallet#83).
 *
 * # Why a console reads its own refusal back
 *
 * Invariant 8 says `Authorize` writes exactly one decision row, every time,
 * allow or refuse. `DeniedView` used to render that guarantee as a fact —
 * "Recorded: Yes — authz_decisions row written" — beside a reference nobody had
 * resolved. #32 removed the claim, because a console asserting an invariant it
 * has not checked has turned the invariant into decoration, and the assertion
 * fails in exactly the case anyone would ever read the panel closely: the day
 * the row is missing, it would still have said the row was there.
 *
 * Not checking was the honest half-measure. This module is the other half: the
 * reference the operator is being handed is resolved, and the panel reports
 * what came back.
 *
 * # Three answers, and they are not one answer
 *
 * - **`recorded`** — the row was read. What it says is quotable, and what it
 *   says about itself (shadow, superseded) changes what an operator should tell
 *   a customer completely.
 * - **`absent`** — the explorer's own handler answered "no decision with that
 *   reference". That is invariant 8 failing, and it is a finding: a refusal
 *   whose decision row does not exist cannot be reconstructed by anyone, ever.
 *   It is deliberately not folded in with the failures below, because the panel
 *   has to read as alarming rather than as a shrug.
 * - **`unverified`** — the lookup did not answer: refused, unreachable, 500,
 *   or a 404 that did not come from the handler at all. We learned nothing
 *   about the row. Saying "missing" here would raise a false alarm about the
 *   most serious invariant in the authorisation seam, which is how an alarm
 *   stops being believed.
 *
 * The line between the last two is a body check, not a status check, and it
 * matters more than it looks: the decisions route is registered only when the
 * backend was wired with a decision source (`admin_routes.go`), so on a
 * deployment without it the request falls through to the mux's own bare-text
 * 404. Same status code, opposite meanings — "this refusal was never recorded"
 * versus "this deployment cannot answer that question".
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_review.go decisionView
// ---------------------------------------------------------------------------

/** One contributing factor. `authz.Reason`, as the explorer renders it. */
export interface DecisionReasonView {
  readonly code: string
  readonly detail?: string
  readonly limit_minor?: number
  readonly used_minor?: number
  readonly requested_minor?: number
}

/** What would unblock the customer. `authz.NextAction`. */
export interface DecisionNextActionView {
  readonly kind: string
  readonly missing?: readonly string[]
  readonly retry_after?: string
}

/** Minor units and an explicit ISO code, always together (invariants 3 and 4). */
export interface DecisionMoneyView {
  /** A JSON number or a decimal string. See MoneyView in dashboard.ts. */
  readonly amount_minor: MinorWire
  readonly currency: string
}

export interface DecisionView {
  readonly decision_ref: string

  readonly outcome: string
  readonly engine: string
  readonly flow: string

  readonly primary_reason: string
  /**
   * `null` on the wire, not `[]`, when the row recorded none — a Go nil slice
   * marshals to null and the handler does not normalise this one the way it
   * normalises the two snapshots.
   */
  readonly reasons: readonly DecisionReasonView[] | null

  /** A rule that was not enforcing. The customer was not affected by it. */
  readonly shadow: boolean

  readonly amount?: DecisionMoneyView

  readonly customer_id?: number
  readonly account_ref?: string
  readonly idempotency_key?: string

  readonly next_action?: DecisionNextActionView

  /** Resolved policy rows AND their versions, and the inputs as read. */
  readonly policy_snapshot: Readonly<Record<string, unknown>>
  readonly input_snapshot: Readonly<Record<string, unknown>>

  readonly supersedes?: string
  readonly superseded_by?: string

  /** The handler's convenience: `superseded_by === ""`. */
  readonly current: boolean

  readonly at: string
}

/** The path for one reference. The client adds the `/v1/admin` prefix. */
export function decisionPath(ref: string): string {
  return `/decisions/${encodeURIComponent(ref)}`
}

// ---------------------------------------------------------------------------
// What a read row says, in the words the panel renders
// ---------------------------------------------------------------------------

/**
 * The amount on the decision, when there is one this console may state.
 *
 * Three states rather than a nullable string, because an amount the backend
 * sent and we could not read is not the same as no amount: the first is a
 * defect worth naming on screen, the second is an ordinary decision that was
 * not about a sum of money.
 */
export type SummarisedAmount =
  | { readonly kind: 'none' }
  | { readonly kind: 'stated'; readonly text: string; readonly currency: string }
  | { readonly kind: 'unstateable'; readonly reason: string }

export interface DecisionSummary {
  /** The outcome as recorded, or a note that it is not one of the four. */
  readonly outcome: string
  /** Which check produced it, and on what flow. */
  readonly attribution: string
  /** The reason an operator quotes. */
  readonly reason: string
  /** When it was decided, in the format the console draws timestamps in. */
  readonly at: string
  /** A rule that was not enforcing: the decision did not affect the customer. */
  readonly shadow: boolean
  /** Non-null when this row must not be quoted as current. */
  readonly superseded: string | null
  readonly amount: SummarisedAmount
  /** Non-null when the row is missing what makes it replayable (invariant 9). */
  readonly snapshotGap: string | null
}

/** What the lookup found. Every branch resolves; none of them throws. */
export type DecisionLookup =
  | { readonly kind: 'recorded'; readonly ref: string; readonly summary: DecisionSummary }
  | { readonly kind: 'absent'; readonly ref: string }
  | { readonly kind: 'unverified'; readonly ref: string; readonly reason: string }

/**
 * The state of the check, as a panel has to render it.
 *
 * Wider than `DecisionLookup` by the two states that are not answers: the read
 * is in flight, or there was no reference to read. Both are cases where the
 * panel must say something, and neither may borrow the wording of a case where
 * we actually looked.
 */
export type DecisionCheck =
  | DecisionLookup
  | { readonly kind: 'checking' }
  | { readonly kind: 'no_ref' }

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

/** A trimmed string, or nothing, whatever the wire actually sent. */
function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * An integer we can state exactly, or nothing.
 *
 * `JSON.parse` hands back a double for a Go `int64`, so a value past 2^53 has
 * already lost digits by the time it reaches here. Same posture as
 * `dashboard.ts`: a number that is wrong in its last places and looks entirely
 * plausible is worse than a stated absence.
 */
function exactInteger(value: unknown): number | null {
  return exactMinor(value)
}

/**
 * When the decision was taken.
 *
 * A Go `time.Time` always serialises, so a row whose timestamp was never set
 * arrives as `0001-01-01T00:00:00Z` — a perfectly parseable date that renders
 * as year one. Dating a decision to the first century is a figure nobody read,
 * so it is reported as the absence it is.
 */
function decidedAt(raw: string): string {
  if (raw === '') return 'the row carries no timestamp'
  const at = new Date(raw)
  if (Number.isNaN(at.getTime())) return `${raw} — a timestamp this console cannot read`
  if (at.getUTCFullYear() < 2000) return `${raw} — a zero timestamp, so the row is not dated`
  return readAtStamp(at)
}

/** How many keys a snapshot carries, tolerating a wire that sent no object. */
function keyCount(snapshot: unknown): number {
  if (typeof snapshot !== 'object' || snapshot === null) return 0
  return Object.keys(snapshot).length
}

/**
 * The amount, only if it can be stated with its currency.
 *
 * `formatMinor` throws on anything that is not three letters, and it is right
 * to (invariant 4) — but it is called during render, so the predicate is asked
 * here, before the panel is built, exactly as the balance legs do it.
 */
function summariseAmount(amount: DecisionMoneyView | undefined): SummarisedAmount {
  if (amount === undefined) return { kind: 'none' }

  const minor = exactInteger(amount.amount_minor)
  if (minor === null) {
    return {
      kind: 'unstateable',
      reason: 'the row carries an amount this console cannot state exactly',
    }
  }
  if (!isCurrencyCode(amount.currency)) {
    return {
      kind: 'unstateable',
      reason:
        text(amount.currency) === ''
          ? 'the row carries an amount with no currency at all (invariant 4)'
          : `the row carries an amount in ${JSON.stringify(amount.currency)}, which is not an ISO-4217 code (invariant 4)`,
    }
  }

  const currency = amount.currency.trim().toUpperCase()
  return { kind: 'stated', text: formatMinor(minor, currency), currency }
}

/**
 * What the row says, phrased for the panel.
 *
 * Pure and separate from the fetch, so every branch is table-testable without a
 * client (CLAUDE.md Part IV — decision functions do no I/O).
 */
export function summariseDecision(d: DecisionView): DecisionSummary {
  const rawOutcome = text(d.outcome)
  const outcome =
    rawOutcome === ''
      ? 'not stated on the row'
      : isAuthzOutcome(rawOutcome)
        ? rawOutcome
        : `${JSON.stringify(rawOutcome)} — an outcome this console does not know`

  const engine = text(d.engine)
  const flow = text(d.flow)
  const attribution =
    engine === '' && flow === ''
      ? 'the row names neither an engine nor a flow'
      : `${engine === '' ? 'engine not stated' : `${engine} engine`} · ${flow === '' ? 'flow not stated' : flow}`

  const primary = text(d.primary_reason)
  const contributing = Array.isArray(d.reasons) ? d.reasons.length : 0
  const reason =
    primary !== ''
      ? primary
      : contributing === 0
        ? 'the row records no reason at all'
        : `no primary reason — ${String(contributing)} contributing reason${contributing === 1 ? '' : 's'} recorded`

  // Two sources for one judgement, and the pessimistic one wins. A row the
  // backend marked as no longer current must not be quoted as current even if
  // it failed to say what replaced it — quoting a corrected decision is how an
  // operator tells a customer the wrong thing confidently.
  const supersededBy = text(d.superseded_by)
  const currentKnown = typeof d.current === 'boolean'
  const superseded =
    supersededBy !== ''
      ? `superseded by ${supersededBy} — do not quote this decision as current`
      : currentKnown && !d.current
        ? 'marked as no longer current, and the row does not name what replaced it'
        : null

  // Invariant 9: the snapshots are what make a decision replayable six months
  // on. A row carrying neither is not evidence of anything, and the panel is
  // the place that says so — nobody opens the explorer for a row they were told
  // is complete.
  const policy = keyCount(d.policy_snapshot)
  const inputs = keyCount(d.input_snapshot)
  const snapshotGap =
    policy === 0 && inputs === 0
      ? 'the row carries neither a policy nor an input snapshot, so it cannot be replayed (invariant 9)'
      : policy === 0
        ? 'the row carries no policy snapshot, so the rule it applied cannot be reconstructed (invariant 9)'
        : inputs === 0
          ? 'the row carries no input snapshot, so what it was given cannot be reconstructed (invariant 9)'
          : null

  return {
    outcome,
    attribution,
    reason,
    at: decidedAt(text(d.at)),
    shadow: d.shadow === true,
    superseded,
    amount: summariseAmount(d.amount),
    snapshotGap,
  }
}

function isDecisionView(payload: unknown): payload is DecisionView {
  if (typeof payload !== 'object' || payload === null) return false
  const d = payload as Partial<DecisionView>
  return typeof d.decision_ref === 'string' && typeof d.outcome === 'string'
}

/**
 * The handler's own "no such decision", as opposed to any other 404.
 *
 * The explorer's not-found body carries `code: "not_found"`. A 404 without it
 * did not come from the handler — the likeliest cause is a deployment where the
 * decision source was never wired, so the route is not registered and the mux
 * answers with bare text. Treating that as a missing row would report an
 * invariant-8 breach on a backend that simply cannot answer.
 */
function isHandlerNotFound(error: ApiError): boolean {
  return error.status === 404 && error.code === 'not_found'
}

/** Why a status other than a handled 404 tells us nothing about the row. */
function whyUnverified(error: ApiError): string {
  const code = error.code === null ? '' : ` (${error.code})`
  const answered = `the decision explorer answered ${String(error.status)}${code}`

  if (error.status === 401) return `${answered} — the session is not authenticated`
  if (error.status === 403) {
    return `${answered} — reading decisions needs a permission this session does not hold`
  }
  if (error.status === 404) {
    return `${answered} without the handler's not-found code, so the endpoint may not be deployed here`
  }
  return answered
}

/**
 * Resolves one reference.
 *
 * Resolves for every answer the backend gives, per the loader contract in
 * `provenance.ts`: an endpoint that refused, failed or does not exist is
 * something the panel reports, not an exception that collapses it. An abort is
 * the one thing that propagates — a navigation away is a cancellation, not a
 * finding about the row.
 */
export async function readDecision(api: ApiClient, ref: string): Promise<DecisionLookup> {
  const wanted = ref.trim()
  if (wanted === '') {
    return {
      kind: 'unverified',
      ref,
      reason: 'the reference was empty, so there was nothing to look up',
    }
  }

  let payload: unknown
  try {
    payload = await api.get<unknown>(decisionPath(wanted))
  } catch (error) {
    if (error instanceof ApiError) {
      return isHandlerNotFound(error)
        ? { kind: 'absent', ref: wanted }
        : { kind: 'unverified', ref: wanted, reason: whyUnverified(error) }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return {
      kind: 'unverified',
      ref: wanted,
      reason: 'the decision explorer could not be reached',
    }
  }

  if (!isDecisionView(payload)) {
    return {
      kind: 'unverified',
      ref: wanted,
      reason: 'the decision explorer answered with a shape this console does not know',
    }
  }

  // The row has to be the one we asked about. Rendering another row's outcome
  // under this reference would be the most convincing wrong answer the panel
  // could give — every word of it read from the backend, and about a different
  // refusal.
  const answered = text(payload.decision_ref)
  if (answered !== wanted) {
    return {
      kind: 'unverified',
      ref: wanted,
      reason: `the decision explorer answered with ${JSON.stringify(answered)}, which is not the reference that was looked up`,
    }
  }

  return { kind: 'recorded', ref: wanted, summary: summariseDecision(payload) }
}
